import './bet-controls.scss';

import NiceModal from '@ebay/nice-modal-react';
import { memo, useEffect, useState } from 'react';
import { shallowEqual } from 'react-redux';

import { useAppDispatch, useAppSelector } from '@/app/store/hooks';
import { jettonActions,selectBetPanelState, selectIsAutoplayActive } from '@/entities/jetton';
import { MODALS } from '@/shared/config/modals';
import { floorAmountForCurrency,getGameCurrencyLimits } from '@/shared/lib/currency';
import { BetPanel } from '@/shared/ui/BetPanel';
import { GameActionButton } from '@/shared/ui/GameActionButton';

import { JettonCashOutButton } from './CashOutButton';
import { getBtnConfig } from './getBtnConfig';

export const BetControls = memo(() => {
  const dispatch = useAppDispatch();
  const { status, myBet, myCashout, balance, selectedCurrency } = useAppSelector(
    selectBetPanelState,
    shallowEqual,
  );

  const [inputValue, setInputValue] = useState('1');

  const autoplayActive = useAppSelector(selectIsAutoplayActive);
  const alreadyBet = !!myBet;
  const alreadyCashedOut = !!myBet?.cashoutMultiplier;
  const betAmount = parseFloat(inputValue) || 0;
  const { minBet, maxBet } = getGameCurrencyLimits('jetton', selectedCurrency);

  const isWaiting = status === 'waiting';
  const isCountdown = status === 'countdown';
  const canCashout = status === 'flying' && alreadyBet && !alreadyCashedOut;
  const inputsActive = (isWaiting || isCountdown) && !alreadyBet;

  const setAmount = (v: number) => {
    const clamped = Math.max(0, Math.min(v, Math.min(maxBet, balance)));
    const normalized = floorAmountForCurrency(clamped, selectedCurrency);
    setInputValue(normalized > 0 ? String(normalized) : '');
  };

  useEffect(() => {
    setInputValue(String(minBet));
  }, [selectedCurrency, minBet]);

  const isBetValid = betAmount >= minBet && betAmount <= maxBet && betAmount <= balance;

  function handleBet() {
    if (!isBetValid) return;
    dispatch(
      jettonActions.placeBet({
        amount: betAmount,
        currency: selectedCurrency,
      }),
    );
  }

  function handleCashout() {
    dispatch(jettonActions.requestCashout());
  }

  function handleCancel() {
    dispatch(jettonActions.cancelBet());
  }

  function openAutoplay() {
    if (autoplayActive) {
      dispatch(jettonActions.stopAutoplay());
      return;
    }
    NiceModal.show(MODALS.AUTOPLAY, {
      currency: selectedCurrency,
      balance,
      minBet,
      maxBet,
      withCashout: true,
      onStart: (config: { betAmount: number; numberOfGames: number; cashoutMultiplier?: number }) =>
        dispatch(
          jettonActions.startAutoplay({
            betAmount: config.betAmount,
            numberOfGames: config.numberOfGames,
            cashoutMultiplier: config.cashoutMultiplier ?? 2.0,
          }),
        ),
    });
  }

  const btn = getBtnConfig({
    status,
    alreadyBet,
    alreadyCashedOut,
    isBetValid,
    myBet,
    myCashout,
    betAmount,
    selectedCurrency,
  });

  const btnAction =
    btn.action === 'cancel' ? handleCancel : btn.action === 'bet' ? handleBet : undefined;

  return (
    <>
      <BetPanel
        currency={selectedCurrency}
        onCurrencyChange={(currency) => dispatch(jettonActions.setSelectedCurrency(currency))}
        onSelectAmount={setAmount}
        maxAmount={Math.min(maxBet, balance)}
        disabled={alreadyBet || !inputsActive}
        onAutoplay={openAutoplay}
        autoplayActive={autoplayActive}
      />

      {canCashout ? (
        <JettonCashOutButton
          amount={myBet!.amount}
          currency={(myBet!.currency as 'TON' | 'STARS') ?? 'TON'}
          onCashout={handleCashout}
        />
      ) : (
        <GameActionButton
          label={btn.label}
          variant={btn.variant}
          outline={btn.outline}
          disabled={btn.disabled}
          onClick={btnAction}
        />
      )}
    </>
  );
});
